import {submitRequest} from '../../api'
import {runInAction} from 'mobx'
import {mergeDiffDeep, mergeByIds} from '../mobx-utils'
import {navigate} from "./uiFlowEvent";
import {parseAndMergeErrors} from "../error-utils";
import {toObjectIdMap} from "../props-utils";
import { FormBean, IUINavigationInstruction,ServiceResponseData,IUIState, DomainDataValueChangeEvent, IFormBean } from '../../datatypes';

declare global {
    interface Window {
        /** global functions set by AD or CP */
        showBusyOverlay?: Function;
        hideBusyOverlay?: Function;
    }
}

/*
storing these actions here because these actions can be called during onChange events at attribute level as well.
So passing a flag in all places might get tedious. Eg:PaymentAmount attribute calls calculateConvenienceFee.
Note: not adding 'PaymentFacade.calculateConvenienceFee' call here. ToDo: design change backend to handle validation correctly.
 */
const notUpdateActions = ['PaymentFacade.populateBankName', 'PaymentFacade.getClientToken'];

export const ACTION_POPULATE_LIABILITY_INFO = "PaymentFacade.populateLiabilityInfo";
export const ACTION_CALC_CONV_FEE = "PaymentFacade.calculateConvenienceFee";
export const ACTION_SUBMIT_PAYMNENT = "PaymentFacade.submitPayment";

/**
 * does actions contains argument action?
 * 
 * @param actions 
 * @param action
 * @returns  
 */
export function actionsContains(actions:string[], action:string){
    return actions.some(v=> v.indexOf(action)>=0);
}

export function actionContains(s:string, action:string){
    return s && s.indexOf(action)>=0;
}

export async function submitRequests(formBean:FormBean, actions:string[]) {
  let doNotUpdateErrors = false;
  // make sure there is a pair of global show/hide busy overlay functions
  let useExternalOverlay = typeof window.showBusyOverlay === 'function' && typeof window.hideBusyOverlay === 'function';

  try {
    // show overlay for ACTION_POPULATE_LIABILITY_INFO and ACTION_SUBMIT_PAYMNENT which might take relatively long time and switch view
    if(actionsContains(actions, ACTION_POPULATE_LIABILITY_INFO)){
      if(useExternalOverlay){
          // @ts-ignore
        window.showBusyOverlay('Load Liability Record');
      }else{
        formBean.isLoading = true;
        formBean.loadingText = 'Load Liability Record'; 
      }
    }else if(actionsContains(actions, ACTION_SUBMIT_PAYMNENT)){
      if(useExternalOverlay){
          // @ts-ignore
        window.showBusyOverlay('Submit Payment');
      }else{
        formBean.isLoading = true;
        formBean.loadingText = 'Submit Payment'; 
      }
    }

    let response = await submitRequest(formBean, actions);
    if(actions && typeof actions === 'string' && notUpdateActions.includes(actions)) {
      doNotUpdateErrors = true;
    }
    else if(actions && Array.isArray(actions) && actions.length > 0) {
      let noUpdateActs = actions.filter(a => notUpdateActions.includes(a));
      if(noUpdateActs && noUpdateActs.length > 0) doNotUpdateErrors = true;
    }
    await handleApiResponse(formBean, response, doNotUpdateErrors)
  } catch(err) {
    console.log('Something went wrong while submitting requests',err);
    formBean.error5xx();
  } finally {
    if(useExternalOverlay){
        // @ts-ignore
      window.hideBusyOverlay();
    }else{
      formBean.isLoading = false;
      formBean.loadingText = '';
    }
  }
  
}

/**
 * TODO: treat backend errors as extra errors. no need to
 * @param formBean 
 * @param response 
 * @param doNotUpdateErrors  
 */
export async function handleApiResponse(formBean:FormBean, response:ServiceResponseData<IUIState>, doNotUpdateErrors?:boolean) {
  let result = response.result;
  if(!result || !result.domainObject) {
    throw 'No domain object in return'
  }
  if(!doNotUpdateErrors) formBean.hasError = response.error;
  else formBean.hasError = formBean.errorCache.hasError;

  if(result.pluginData && formBean.PluginData) {
    replace(formBean.PluginData, result.pluginData);
  }
  // response has events; result does not have events !!!
  let resultEvents = (result.events || []);
  let updates = resultEvents.filter(e => (e as DomainDataValueChangeEvent).changeType) as DomainDataValueChangeEvent[];
  let newDomainObject = resultEvents.find(e => e === 'NEW_DOMAIN_OBJECT');
  if(newDomainObject){
    runInAction(() => {
      mergeDiffDeep(formBean.DomainObject[formBean.DomainObjectName], result.domainObject);
    });
  } else if(updates.length > 0){
    //do a differential merge on formBean.DomainObject[domainObjectName] and uiState.domainObject
    runInAction(() =>{
      let objectIdMapTarget = toObjectIdMap(formBean.DomainObject);
      let objectIdMapSrc = toObjectIdMap(result.domainObject);
      mergeByIds(objectIdMapTarget, objectIdMapSrc, updates, formBean )
    })
  }

  // dead code !!!
//   if(formBean.eventMap && resultEvents.length > 0 && Object.keys(formBean.eventMap).length > 0){
//     for(let j = 0; j < resultEvents.length; j++) {
//       let event = resultEvents[j];
//       let onEvent = formBean.eventMap[event];
//       if(onEvent){
//         for (let i = 0; i < onEvent.length; i++) {
//           //promise.resolve will wait if the handler is async otherwise do it normally
//           await Promise.resolve(onEvent[i]());
//         }
//       }
//     }
//   }

  formBean.contextualErrors = {};
  formBean.NavigationInstructionList = response.result.navigationInstruction;
  let navigationInstructionList = formBean.NavigationInstructionList;
  if(navigationInstructionList && navigationInstructionList.length > 0){
    generateNavigation(formBean, navigationInstructionList);
    return;
  }

  /*
  2 scenarios.
  1: update errors - when backend call returns validation, we have to update all errors with the ones backend sends.
  2: dont update errors - when backend call doesnt do validation, it returns empty error response. and if we update errors, this response overrides the existing errors
  on the page which is incorrect.
  So everytime a call like this is made, we maintain a copy of existing errors in errorCache.
  This errorCache errors can be added/deleted by frontend based on user changes (chaning value to empty/invalid etc).

  Note: formBean.errorCache is a copy of response.errorMessage.
   */
  if(response.error && !doNotUpdateErrors) {
    formBean.errorCache = {hasError: response.error, errorMessage: response.errorMessage};
    parseAndMergeErrors(formBean, response.errorMessage!);

  }else if (doNotUpdateErrors){
    if(response.error && response.errorMessage ){      //have to append these errors to the errorCache
      if(formBean.errorCache.errorMessage)             //if some errMessage already in errorCache then have to append.
        formBean.errorCache.errorMessage = formBean.errorCache.errorMessage.concat(response.errorMessage);
      else                                             //errorMessage doesnt exist in errorCache so have to add it.
        formBean.errorCache.errorMessage = response.errorMessage;

      parseAndMergeErrors(formBean, formBean.errorCache.errorMessage);
    }
    else if(!response.error && formBean.errorCache.errorMessage && formBean.errorCache.errorMessage.length > 0)
      parseAndMergeErrors(formBean, formBean.errorCache.errorMessage);
  }

}

function generateNavigation(formBean:FormBean, navigationInstructionList:IUINavigationInstruction[]){
  for(let instruction of navigationInstructionList) {
    let viewNames = formBean.ViewMetadataList.map((v:any) => v.name);
    if(!instruction.flowName && instruction.viewName){
        // switch view within the same flow
      navigate(formBean, instruction.viewName, viewNames, true);
    }
  }
}

/**
 * to created a deep object out of error Messages that are returned
 * @param errorMessages
 * @return {{}}
 */
function toErrorObject(errorMessages:any[]) {
  let errorObj = {} as {[key:string]: any};
  errorMessages.forEach(message => {
    if(!message.objectName) {
      //only concerned with domain object based errors
      return;
    }
    let path = message.objectName.split('.');
    let parent = errorObj;

    for(let i =0; i < path.length; i++) {
      let currentObjName = path[i];
      //array based
      if(currentObjName.indexOf('[') > 0) {
        let bracketStartIdx = currentObjName.indexOf('[');
        let bracketEndIdx = currentObjName.indexOf(']');
        let index = Number(currentObjName.substring(bracketStartIdx+1, bracketEndIdx));
        currentObjName = currentObjName.substring(0, bracketStartIdx);
        if(!parent[currentObjName]) {
          parent[currentObjName] = [];
        }
        if(!parent[currentObjName][index]){
          parent[currentObjName][index] = {};
        }

        parent=parent[currentObjName][index];

      } else {
        if(!parent[currentObjName]){
          parent[currentObjName] = {};
        }
        parent = parent[currentObjName];
      }

    }

    parent[message.attributeName] = message;

  });
  return errorObj;
}

/**
 * replace value/object in the target with the one in the source
 * @param {*} target 
 * @param {*} {...source} 
 */
function replace(target:{[key:string]:any}, {...source}) {
  for(let objectName in source) {
    target[objectName] = source[objectName];
  }
}

